"use client";

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Controller, useFormContext } from "react-hook-form";
import { CheckIcon } from "@/components/ui/CheckIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { XIcon } from "@/components/ui/XIcon";
import {
  REFERRER_MESSAGE_KEY,
  type ReferrerStatus,
} from "@/hooks/checkout/useReferrerValidation";
import { cn } from "@/lib/utils";

interface ReferralAddressFieldProps {
  /** RHF field name holding the referrer address (differs per form). */
  name: string;
  /** RHF field name of the "I have a referrer" checkbox. */
  checkboxName: string;
  /** Live status from the page's single `useParentResolution` instance. */
  status: ReferrerStatus;
}

/** Statuses that mean "we asked the chain and the answer was no". */
const FAILED: ReferrerStatus[] = [
  "invalid",
  "self",
  "not-member",
  "full",
  "unreachable",
];

/**
 * Referrer address input with live on-chain validation.
 *
 * UX mirrors the nickname-availability check in `LoginForm`: type, get a
 * spinner, then a check or a cross plus a reason. There is deliberately **no
 * "submit referral" button** — it made users think the address had been
 * accepted when all it did was run a regex.
 */
export const ReferralAddressField = ({
  name,
  checkboxName,
  status,
}: ReferralAddressFieldProps) => {
  const t = useTranslations("checkout");
  const { control, watch } = useFormContext();
  const useReferral = watch(checkboxName);

  // Guest mode is rendered by <ReferralSection>; this field is the referrer
  // path only.
  if (!useReferral) return null;

  const hasFailed = FAILED.includes(status);
  const messageKey = `referrer.${REFERRER_MESSAGE_KEY[status]}`;

  const adornment = (
    <div className="flex items-center justify-center size-5">
      <AnimatePresence mode="wait" initial={false}>
        {status === "checking" && (
          <motion.div
            key="spinner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <Spinner className="size-5 text-text-secondary" />
          </motion.div>
        )}
        {status === "valid" && (
          <motion.div
            key="check"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <CheckIcon size={20} className="text-semantic-success" />
          </motion.div>
        )}
        {hasFailed && (
          <motion.div
            key="x"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <XIcon size={20} className="text-semantic-error" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="space-y-2 w-full">
          <Label htmlFor={name}>{t("referrer.label")}</Label>
          <Input
            {...field}
            value={field.value ?? ""}
            id={name}
            type="text"
            dir="ltr"
            placeholder="0x..."
            autoComplete="off"
            spellCheck={false}
            className={cn({
              "border-semantic-error": hasFailed,
              "border-semantic-success": status === "valid",
            })}
            endAdornment={adornment}
          />
          {messageKey && status !== "idle" ? (
            <p
              className={cn("text-xs leading-tight ms-1.5", {
                "text-semantic-error": hasFailed,
                "text-semantic-success": status === "valid",
                "text-text-secondary": status === "checking",
              })}
            >
              {t(messageKey)}
            </p>
          ) : null}
        </div>
      )}
    />
  );
};
